import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inventoryService } from '../../services/inventoryService';
import { paginationFromThunkAction } from '../../utils/paginationUtils';

// ============================================================================
// ASYNC THUNKS
// ============================================================================

export const fetchProducts = createAsyncThunk(
  'inventory/fetchProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getProducts(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'inventory/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getProductById(productId);
      return response;
    } catch (error) {
      console.error('Error in fetchProductById:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch product');
    }
  }
);

export const createProduct = createAsyncThunk(
  'inventory/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const response = await inventoryService.createProduct(productData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'inventory/updateProduct',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const response = await inventoryService.updateProduct(productId, productData);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'inventory/deleteProduct',
  async (productId, { rejectWithValue }) => {
    try {
      await inventoryService.deleteProduct(productId);
      return { productId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete product');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'inventory/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getCategories();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const fetchProductMeters = createAsyncThunk(
  'inventory/fetchProductMeters',
  async ({ productId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getProductMeters(productId, params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch meters');
    }
  }
);

export const addMeterToProduct = createAsyncThunk(
  'inventory/addMeterToProduct',
  async ({ productId, meterData }, { rejectWithValue }) => {
    try {
      const response = await inventoryService.addMeterToProduct(productId, meterData);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add meter');
    }
  }
);

export const fetchMeterAssignmentData = createAsyncThunk(
  'inventory/fetchMeterAssignmentData',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getMeterAssignmentData(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assignment data');
    }
  }
);

// ============================================================================
// SLICE
// ============================================================================

const initialState = {
  products: [],
  currentProduct: null,
  categories: [],
  meters: [],
  meterAssignmentData: {
    practices: [],
    patients: [],
    serialNumbers: [],
    extensions: []
  },
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  },
  metersPagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  },
  isListLoading: false,
  isProductDetailLoading: false,
  isSaving: false,
  isDeleting: false,
  /** { index, product } — restore on deleteProduct.rejected */
  optimisticDeleteBackup: null,
  metersLoading: false,
  error: null,
  successMessage: null
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setCurrentProduct: (state, action) => {
      state.currentProduct = action.payload;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.isListLoading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.products = action.payload.data || [];
        state.pagination = paginationFromThunkAction(action, action.payload.pagination || state.pagination);
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.isListLoading = false;
        state.error = action.payload;
      });

    // Fetch product by ID (does not toggle list loading — avoids list skeleton on "Edit" from list)
    builder
      .addCase(fetchProductById.pending, (state) => {
        state.isProductDetailLoading = true;
        state.error = null;
        state.currentProduct = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.isProductDetailLoading = false;
        // Handle both response.data.data and response.data structures
        state.currentProduct = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.isProductDetailLoading = false;
        state.error = action.payload;
        state.currentProduct = null;
      });

    // Create product
    builder
      .addCase(createProduct.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isSaving = false;
        state.successMessage = action.payload.message || 'Product created successfully';
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });

    // Update product
    builder
      .addCase(updateProduct.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isSaving = false;
        state.successMessage = action.payload.message || 'Product updated successfully';
        if (state.currentProduct && state.currentProduct.id === action.payload.productId) {
          state.currentProduct = { ...state.currentProduct, ...action.payload };
        }
        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      });

    // Delete product (optimistic remove on pending; rollback on reject)
    builder
      .addCase(deleteProduct.pending, (state, action) => {
        const rawId = action.meta.arg;
        const pid = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
        const idx = state.products.findIndex((p) => Number(p.id) === Number(pid));
        if (idx !== -1) {
          state.optimisticDeleteBackup = { index: idx, product: state.products[idx] };
          state.products.splice(idx, 1);
        } else {
          state.optimisticDeleteBackup = null;
        }
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.optimisticDeleteBackup = null;
        const pid = action.payload.productId;
        state.products = state.products.filter((p) => Number(p.id) !== Number(pid));
        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isDeleting = false;
        if (state.optimisticDeleteBackup) {
          const { index, product } = state.optimisticDeleteBackup;
          const insertAt = Math.min(Math.max(0, index), state.products.length);
          state.products.splice(insertAt, 0, product);
          state.optimisticDeleteBackup = null;
        }
        state.error = action.payload;
      });

    // Fetch categories
    builder
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.data || [];
      });

    // Fetch product meters
    builder
      .addCase(fetchProductMeters.pending, (state) => {
        state.metersLoading = true;
        state.error = null;
      })
      .addCase(fetchProductMeters.fulfilled, (state, action) => {
        state.metersLoading = false;
        state.meters = action.payload.data || [];
        state.metersPagination = action.payload.pagination || state.metersPagination;
        state.error = null;
      })
      .addCase(fetchProductMeters.rejected, (state, action) => {
        state.metersLoading = false;
        state.error = action.payload;
      });

    // Add meter to product
    builder
      .addCase(addMeterToProduct.fulfilled, (state, action) => {
        state.successMessage = action.payload.message || 'Meter added successfully';
        state.error = null;
      })
      .addCase(addMeterToProduct.rejected, (state, action) => {
        state.error = action.payload;
      });

    // Fetch meter assignment data
    builder
      .addCase(fetchMeterAssignmentData.fulfilled, (state, action) => {
        state.meterAssignmentData = action.payload.data || {
          practices: [],
          patients: [],
          serialNumbers: [],
          extensions: []
        };
      });
  }
});

export const { clearError, clearSuccessMessage, setCurrentProduct, clearCurrentProduct } = inventorySlice.actions;
export default inventorySlice.reducer;

